import { useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  ArrowDown2,
  ArrowRight,
  Clock,
  Danger,
  DocumentUpload,
  Flag,
  ImportCurve,
  Like1,
  Refresh2,
} from "iconsax-reactjs";
import {
  Badge,
  BodyText,
  Button,
  Card,
  CardContent,
  HeadingText,
} from "../../../shared/ui";
import { KybRequirement } from "../types";
import {
  ActionIconButton,
  FlagTargetKind,
  MetricPill,
  RequirementCounts,
  approvedActionColor,
  getRequirementStatusBadgeClassName,
  getRequirementStatusLabel,
  isImageDocument,
  isPdfDocument,
} from "./kybShowReviewShared";

type KybComplianceDocumentsCardProps = {
  activeDocument: KybRequirement | null;
  activeDocumentStatus: string;
  activeDocumentUrl?: string;
  documentRequirements: KybRequirement[];
  hasPreviewableDocument: boolean;
  isActiveDocumentFlagged: boolean;
  isDocumentListOpen: boolean;
  requirementCounts: RequirementCounts;
  reviewingStatus: "APPROVED" | "REJECTED" | null;
  disableAddDocument?: boolean;
  isReadOnly?: boolean;
  onDocumentListOpenChange: (open: boolean) => void;
  onOpenFlagModal: (
    requirement: KybRequirement | null | undefined,
    kind: FlagTargetKind,
  ) => void;
  onOpenAddDocument: () => void;
  onOpenReUploadDocument: (
    requirement: KybRequirement | null | undefined,
  ) => void;
  onOpenIssueDetails: () => void;
  onPreviewOpen: () => void;
  onReviewActiveDocument: (
    status: "APPROVED" | "REJECTED",
    comment: string,
  ) => void;
  onSelectedDocumentChange: (documentId: string) => void;
};

const KybComplianceDocumentsCard = ({
  activeDocument,
  activeDocumentStatus,
  activeDocumentUrl,
  documentRequirements,
  hasPreviewableDocument,
  isActiveDocumentFlagged,
  isDocumentListOpen,
  requirementCounts,
  reviewingStatus,
  disableAddDocument = false,
  isReadOnly = false,
  onDocumentListOpenChange,
  onOpenFlagModal,
  onOpenAddDocument,
  onOpenReUploadDocument,
  onOpenIssueDetails,
  onPreviewOpen,
  onReviewActiveDocument,
  onSelectedDocumentChange,
}: KybComplianceDocumentsCardProps) => {
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isDocumentListOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        onDocumentListOpenChange(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
    };
  }, [isDocumentListOpen, onDocumentListOpenChange]);

  const isActiveDocumentApproved = activeDocumentStatus === "APPROVED";
  const canDownloadActiveDocument = Boolean(activeDocumentUrl);
  const activeDocumentIndex = activeDocument
    ? documentRequirements.findIndex((item) => item.id === activeDocument.id)
    : -1;
  const canGoToPreviousDocument = activeDocumentIndex > 0;
  const canGoToNextDocument =
    activeDocumentIndex >= 0 &&
    activeDocumentIndex < documentRequirements.length - 1;
  const goToDocumentAtIndex = (index: number) => {
    const document = documentRequirements[index];
    if (!document) return;

    onSelectedDocumentChange(document.id);
  };

  return (
    <Card className="overflow-hidden rounded-[16px] border border-[#EBEDF9] shadow-none">
      <CardContent className="p-6">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex items-center gap-4 ">
            <HeadingText className="aku-heading-h5-medium text-[#161827]">
              Documents
            </HeadingText>
            <div className="bg-[#F3F4FB] rounded-full flex items-center gap-4 px-3 py-1">
              <MetricPill
                value={requirementCounts.pending}
                icon={<Clock size={16} color="#BFC3E3" variant="Bold" />}
                iconClassName="text-[#BFC3E3]"
              />
              <MetricPill
                value={requirementCounts.approved}
                icon={
                  <Like1 size={16} color={approvedActionColor} variant="Bold" />
                }
                iconClassName="text-[#1DCB1C]"
              />
              <MetricPill
                value={requirementCounts.rejected}
                icon={<Flag size={16} color="#F6A41B" variant="Bold" />}
                iconClassName="text-[#F6A41B]"
              />
            </div>
          </div>

          {!isReadOnly ? (
            <Button
              variant="outline"
              disabled={disableAddDocument || reviewingStatus !== null}
              onClick={onOpenAddDocument}
              className="rounded-[6.4px] border-[#F2F3FC] border-[0.8px solid] bg-white px-4 h-10 aku-body-normal-medium text-[#161827] hover:bg-[#F7F8FD]"
            >
              <DocumentUpload size={16} />
              Add document
            </Button>
          ) : null}
        </div>

        <div className="mt-6 rounded-[20px] border border-dashed border-[#D9DDF6] bg-[#F7F8FD] p-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div ref={dropdownRef} className="relative">
              <button
                type="button"
                disabled={documentRequirements.length === 0}
                onClick={() => onDocumentListOpenChange(!isDocumentListOpen)}
                className="flex items-center gap-2 rounded-[8px] px-1 py-1 text-left hover:bg-[#F0F2FB] disabled:cursor-not-allowed disabled:text-[#A0A6C5] disabled:hover:bg-transparent"
              >
                <HeadingText className="aku-body-small-medium text-[#161827]">
                  {activeDocument?.label || activeDocument?.key || "No submitted document"}
                </HeadingText>
                <ArrowDown2
                  size={18}
                  variant="Bold"
                  className="text-[#6F739B]"
                />
              </button>

              {isDocumentListOpen && documentRequirements.length > 0 ? (
                <div className="absolute left-0 top-[calc(100%+8px)] z-20 max-h-[280px] min-w-[280px] max-w-[420px] overflow-y-auto rounded-[12px] border border-[#E4E7F4] bg-white p-2 shadow-[0_12px_32px_rgba(15,20,40,0.12)]">
                  {documentRequirements.map((item) => {
                    const isActive = item.id === activeDocument?.id;
                    const labelText = item.label || item.key;

                    return (
                      <button
                        key={item.id}
                        type="button"
                        title={labelText}
                        onClick={() => {
                          onSelectedDocumentChange(item.id);
                          onDocumentListOpenChange(false);
                        }}
                        className={`flex w-full items-center justify-between gap-4 rounded-[8px] px-3 py-2 text-left transition-colors ${
                          isActive
                            ? "bg-[#F1ECFF] text-[#5E01D6]"
                            : "text-[#383B56] hover:bg-[#F7F8FD]"
                        }`}
                      >
                        <span className="truncate aku-body-small-regular">
                          {labelText}
                        </span>
                        <span className="shrink-0 aku-body-small-regular text-[#6F739B]">
                          {item.status}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </div>

            <Badge
              className={`h-6 px-3 aku-body-small-regular ${getRequirementStatusBadgeClassName(activeDocument?.status)}`}
            >
              {getRequirementStatusLabel(activeDocument?.status)}
            </Badge>
          </div>

          <div className="mt-5 flex items-center justify-between">
            <div className="flex items-center gap-0">
              <button
                type="button"
                disabled={!canGoToPreviousDocument}
                onClick={() => goToDocumentAtIndex(activeDocumentIndex - 1)}
                className="grid h-9 w-9 place-items-center rounded-l-[7.228px] rounded-r-none border-l-[0.941px] border-r-1 border-t-[0.941px] border-r border-b-[0.941px] border-[#E4E7F4] bg-white text-[#6F739B] hover:bg-[#FDFDFF] disabled:cursor-not-allowed disabled:text-[#C0C5E3] disabled:hover:bg-white"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                type="button"
                disabled={!canGoToNextDocument}
                onClick={() => goToDocumentAtIndex(activeDocumentIndex + 1)}
                className="grid h-9 w-9 place-items-center rounded-r-[7.228px] rounded-l-none border-r-[0.941px] border-l-1 border-t-[0.941px] border-b-[0.941px] border-[#E4E7F4] bg-white text-[#6F739B] hover:bg-[#FDFDFF] disabled:cursor-not-allowed disabled:text-[#C0C5E3] disabled:hover:bg-white"
              >
                <ChevronRight size={18} />
              </button>
            </div>

            <div className="flex items-center gap-2">
              <ActionIconButton
                title="Approve document"
                disabled={
                  isReadOnly ||
                  isActiveDocumentApproved ||
                  !activeDocument ||
                  reviewingStatus !== null
                }
                onClick={() => onReviewActiveDocument("APPROVED", "Approved")}
              >
                <Like1
                  size={22}
                  variant={isActiveDocumentApproved ? "Bold" : "Linear"}
                  color={
                    isActiveDocumentApproved ? approvedActionColor : "#2B2F49"
                  }
                />
              </ActionIconButton>
              <ActionIconButton
                title="Flag document"
                disabled={
                  isReadOnly ||
                  isActiveDocumentFlagged ||
                  !activeDocument ||
                  reviewingStatus !== null
                }
                onClick={() => onOpenFlagModal(activeDocument, "document")}
              >
                <Flag
                  size={20}
                  variant={isActiveDocumentFlagged ? "Bold" : "Linear"}
                  color={isActiveDocumentFlagged ? "#F6A41B" : "#2B2F49"}
                />
              </ActionIconButton>
              <ActionIconButton
                title="Download document"
                disabled={!canDownloadActiveDocument}
                onClick={() => {
                  if (!activeDocumentUrl) return;
                  window.open(activeDocumentUrl, "_blank", "noopener,noreferrer");
                }}
              >
                <ImportCurve size={20} />
              </ActionIconButton>
              {isActiveDocumentFlagged ? (
                <ActionIconButton
                  title="Re-upload document"
                  disabled={!activeDocument || reviewingStatus !== null}
                  onClick={() => onOpenReUploadDocument(activeDocument)}
                >
                  <Refresh2 size={20} />
                </ActionIconButton>
              ) : null}
            </div>
          </div>

          <div className="mt-6 overflow-hidden rounded-[24px] border border-dashed border-[#D9DDF6] bg-white">
            {hasPreviewableDocument && activeDocumentUrl ? (
              <div className="relative">
                {!isActiveDocumentFlagged ? (
                  <button
                    type="button"
                    onClick={onPreviewOpen}
                    className="absolute right-4 top-4 z-10 rounded-[8px] bg-[rgba(22,24,39,0.82)] px-3 py-2 aku-body-small-regular text-white hover:bg-[rgba(22,24,39,0.92)]"
                  >
                    View full
                  </button>
                ) : null}
                {isActiveDocumentFlagged ? (
                  <div className="absolute left-0 right-0 top-0 z-[5] flex items-start justify-between gap-4 rounded-t-[24px] bg-[#FFF8E5] px-6 py-4">
                    <div className="flex items-center gap-3">
                      <span className="">
                        <Danger size={18} color="#DB6704" variant="Bold" />
                      </span>
                      <HeadingText className="aku-body-small-medium text-[#161827]">
                        This document was flagged
                      </HeadingText>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <button
                        type="button"
                        onClick={onOpenIssueDetails}
                        className="aku-label-medium text-[#461502] hover:text-[#431407] flex items-center gap-3"
                      >
                        View details <ArrowRight color="#93360D" size={20} />
                      </button>
                      <button
                        type="button"
                        onClick={onPreviewOpen}
                        className="rounded-[8px] bg-[rgba(22,24,39,0.82)] px-3 py-2 aku-body-small-regular text-white hover:bg-[rgba(22,24,39,0.92)]"
                      >
                        View full
                      </button>
                    </div>
                  </div>
                ) : null}
                {isImageDocument(activeDocumentUrl) ? (
                  <div
                    className={`flex max-h-[640px] min-h-[480px] items-center justify-center bg-[#FCFCFF] p-4 ${
                      isActiveDocumentFlagged ? "pt-32" : ""
                    }`}
                  >
                    <img
                      src={activeDocumentUrl}
                      alt={activeDocument?.key || "KYB document"}
                      className="max-h-[600px] w-auto max-w-full object-contain"
                    />
                  </div>
                ) : isPdfDocument(activeDocumentUrl) ? (
                  <div
                    className={`h-[640px] bg-white ${
                      isActiveDocumentFlagged ? "pt-28" : ""
                    }`}
                  >
                    <iframe
                      title={activeDocument?.key || "KYB document"}
                      src={activeDocumentUrl}
                      className="h-full w-full bg-white"
                    />
                  </div>
                ) : (
                  <div className="flex min-h-[480px] flex-col items-center justify-center gap-4 bg-[#FCFCFF] px-6 py-10 text-center">
                    <DocumentUpload size={32} className="text-[#6F739B]" />
                    <div>
                      <HeadingText className="aku-body-small-medium text-[#161827]">
                        Preview unavailable
                      </HeadingText>
                      <BodyText className="mt-1 aku-body-small-regular text-[#6F739B]">
                        Open {activeDocument?.key || "this document"} in a new
                        tab to review it.
                      </BodyText>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      className="h-10 rounded-[8px] border-[#E4E7F4] bg-white px-4 aku-body-small-medium text-[#161827] hover:bg-[#F7F8FD]"
                      onClick={() =>
                        window.open(
                          activeDocumentUrl,
                          "_blank",
                          "noopener,noreferrer",
                        )
                      }
                    >
                      Open document
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex min-h-[480px] flex-col items-center justify-center gap-4 bg-[#FCFCFF] px-6 py-10 text-center">
                <DocumentUpload size={32} className="text-[#6F739B]" />
                <div>
                  <HeadingText className="aku-body-small-medium text-[#161827]">
                    No document available
                  </HeadingText>
                  <BodyText className="mt-1 aku-body-small-regular text-[#6F739B]">
                    This requirement does not have a submitted document yet.
                  </BodyText>
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default KybComplianceDocumentsCard;
